import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Deal } from 'app/models/dto/deal.model';
import { DocumentCategory } from 'app/models/dto/document-category.model';
import { DealApplicationRequirement } from 'app/services/deals/deal-application-requirement.model';
import { AddDocumentCategory, AttachDealDocument, DetachDealDocument, GetDealDocumentRequirements, UploadDealDocument } from 'app/store/deal/deal-documents/deal-document.actions';
import { DealDocumentState } from 'app/store/deal/deal-documents/deal-document.state';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import * as _ from 'lodash';
import { DocumentState } from 'app/store/documents/document.state';
import { Document } from 'app/models/dto/document.model';
import { DealDocumentUtility } from 'app/store/deal/deal-documents/deal-document-utility';
import { SubscribeToBorrowerDocuments } from 'app/store/documents/document.actions';

@Component({
  selector: 'app-deal-document-smart',
  templateUrl: './deal-document-smart.component.html',
  styleUrls: ['./deal-document-smart.component.scss'],
  standalone: false
})
export class DealDocumentSmartComponent implements OnInit, OnDestroy {

  @Input() deal: Deal;
  @Input() readOnly: boolean = false;
  @Select(DocumentState.documents) documents$: Observable<Document[]>;
  @Select(DealDocumentState.uploading) uploadingDocument$: Observable<boolean>;
  @Select(DealDocumentState.loading) loadingRequirements$: Observable<boolean>;

  destroyed$ = new Subject<boolean>();
  documentCategories$: Observable<DocumentCategory[]>;
  requirementsMet$: Observable<boolean>;
  documentRequirements$: Observable<{
    contractRequirements: DealApplicationRequirement[];
    offerRequirements: DealApplicationRequirement[];
  } | never[]>;

  constructor(
    private store: Store,
    private dealDocumentUtility: DealDocumentUtility
  ) { }

  ngOnInit(): void {
    const dealDocumentTypes = this.dealDocumentUtility.docTypes;
    this.store.dispatch(new SubscribeToBorrowerDocuments(this.deal.borrowerId));
    this.store.dispatch(new GetDealDocumentRequirements(this.deal, this.deal.loan_product.id));
    this.documentCategories$ = this.store.select(DocumentState.dealDocumentCategories).pipe(
      takeUntil(this.destroyed$),
      map(filterFn => filterFn(dealDocumentTypes))
    );
    this.documentRequirements$ = this.store.select(DealDocumentState.dealDocumentRequirements).pipe(
      takeUntil(this.destroyed$),
      map(filterFn => filterFn(this.deal.id))
    );
    this.requirementsMet$ = this.store.select(DealDocumentState.requirementsMet).pipe(
      takeUntil(this.destroyed$),
      map(filterFn => filterFn(this.deal.id))
    );
  }

  shouldDisplayContractRequirements() {
    return this.deal.stage !== 'application' && this.deal.status !== 'awaitingResponse' && this.deal.status !== 'declined';
  }

  addDocument(documentCategory: DocumentCategory) {
    this.store.dispatch(new AddDocumentCategory(this.deal.id, documentCategory));
  }

  // eslint-disable-next-line max-len
  uploadDocs(docsToUpload: { category: { display: string, value: string, alias: string, attributeId: number }, documents: FileList }, requirementGroup: string) {
    _.each(docsToUpload.documents, doc => {
      this.store.dispatch(new UploadDealDocument(doc, this.deal.borrowerId, this.deal.id, docsToUpload.category.value, requirementGroup));
    });
  }

  attachDocument(document: Document, requirementGroup: string) {
    this.store.dispatch(new AttachDealDocument(this.deal.id, document, requirementGroup));
  }

  detachDocument(document: Document, requirementGroup: string) {
    this.store.dispatch(new DetachDealDocument(this.deal.id, document, requirementGroup));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
